import { AspectRatio, Box, Center, Flex, HStack, Text } from "@highoutput/ui-components";
import __ from "lodash";
import Image from "next/image";
import { useEffect, useState } from "react";
import { InView } from "react-intersection-observer";
import { useSwipeable } from "react-swipeable";
import type { TestimonialCoreDataFragment } from "~/__generated__/graphql";

import AnimateWrapper from "~/components/animate-wrapper/animate-fade-in-out";
import Rating from "./rating";
type TestimonialsProps = {
  data: TestimonialCoreDataFragment[];
};

export default function Testimonials({ data }: TestimonialsProps) {
  const [index, setIndex] = useState(0);
  const originalArray = data;
  const chunkedArray = __.chunk(originalArray, 3);

  const swipeable = useSwipeable({
    onSwipedLeft() {
      if (index < chunkedArray.length) {
        setIndex((i) => i + 1);
      }
    },
    onSwipedRight() {
      if (index > 1) {
        setIndex((i) => i - 1);
      }
    },
  });

  useEffect(() => {
    return () => {
      setIndex(0);
    };
  }, []);

  if (!data.length) return null;
  const testimonial = data.at(index - 1);
  if (!testimonial) return null;

  return (
    <Box
      transition="height 300ms ease-in-out"
      position={"relative"}
      px={{ xl: "160px", lg: "24px", base: "16px" }}
      mx="auto"
      bgColor="rgba(240, 237, 236, 0.2)"
      py={{
        base: "64px",
        lg: "100px",
        xl: "100px",
      }}
      {...swipeable}
      fontFamily={"'Inter', sans-serif"}
      height="full"
    >
      <Flex direction={"column"} align={"center"} mb={{ lg: "64px", base: "40px" }}>
        <Text
          fontSize={{ xl: "18px", lg: "18px", base: "14px" }}
          lineHeight={{ xl: "18px", lg: "18px", base: "14px" }}
          letterSpacing={"0.05em"}
          color="rgba(13, 48, 80, 0.8)"
          mb="16px"
        >
          TESTIMONIAL
        </Text>
        <Text
          fontFamily={"Georgia"}
          color="#0D3050"
          fontSize={{ xl: "40px", lg: "40px", base: "24px" }}
          lineHeight={{ xl: "44px", lg: "44px", base: "28px" }}
          letterSpacing={"-0.02em"}
          textAlign={"center"}
        >
          Experiences Shared by Our Happy Clients
        </Text>
      </Flex>
      <InView triggerOnce height={"fit-content"}>
        {({ ref, inView }) => (
          <Box
            ref={ref}
            sx={{
              opacity: 0,
              transform: "translateY(10vh)",
              transition: "transform 600ms ease-out, opacity 600ms ease-out",
              willChange: "transform, opacity",
              ...(inView && {
                opacity: 1,
                transform: "none",
              }),
            }}
            position={"relative"}
          >
            <Box position={"relative"} height={{ lg: "495px", base: "1235px" }}>
              {chunkedArray.map((chunk, idx) => {
                return (
                  <AnimateWrapper
                    key={idx}
                    activeIndex={index}
                    defaultIndex={idx}
                    isPrev={index > idx}
                  >
                    <Flex
                      justify={"center"}
                      align={"center"}
                      direction={{ lg: "row", base: "column" }}
                      gap={"24px"}
                      position={"absolute"}
                      w="full"
                    >
                      {chunk.map((d) => {
                        return <Item key={d.id} data={d} />;
                      })}
                    </Flex>
                  </AnimateWrapper>
                );
              })}
            </Box>

            <Center marginTop="40px">
              <Indicator count={chunkedArray.length} value={index} onChange={setIndex} />
            </Center>
          </Box>
        )}
      </InView>
    </Box>
  );
}

type IndicatorProps = {
  count: number;
  value: number;
  onChange: (newValue: number) => void;
};

const increment = (n: number) => n;

function Indicator({ value, count, onChange }: IndicatorProps) {
  return (
    <HStack spacing="16px">
      {new Array(count).fill(null).map((_, key) => (
        <Box
          key={key}
          role="button"
          aria-label={`Go to testimonial page ${increment(key)}`}
          width="10px"
          height="10px"
          rounded="full"
          bgColor={value === increment(key) ? "#F9D209" : "#F0F0F0"}
          cursor="pointer"
          tabIndex={0}
          outline="none"
          transition="background-color 250ms ease-in-out"
          onClick={() => onChange(increment(key))}
        />
      ))}
    </HStack>
  );
}

function Item({ data }: { data: TestimonialCoreDataFragment }) {
  return (
    <Flex
      alignItems="start"
      flexDirection="column"
      border={"1px solid rgba(240, 237, 236, 0.8)"}
      fontFamily="'Inter', sans-serif"
      p={{ lg: "40px", base: "32px" }}
      width={{ xl: "357.94px", lg: "304px" }}
      minHeight={{ lg: "490.33px", base: "390px" }}
      gap={"24px"}
      borderRadius={"8px"}
      bgColor="white"
    >
      <Rating
        value={data.rating}
        readonly
        config={{
          icon: {
            size: {
              width: "20px",
              height: "20px",
            },
          },
        }}
        data-testid={`testimonial.${data.id}.rating`}
      />
      <Text
        maxWidth="800px"
        textAlign="left"
        fontSize={{ xl: "16px", lg: "14px", base: "14px" }}
        lineHeight={{ xl: "24px", lg: "20px", base: "20px" }}
        letterSpacing={"0.02em"}
        color="#0D3050"
        fontFamily="'Inter', sans-serif"
        dangerouslySetInnerHTML={{
          __html: `“${data.feedback.trim()}”`,
        }}
        height={{ lg: "245px", base: "160px" }}
        data-testid={`testimonial.${data.id}.feedback`}
      />

      <Flex align={"center"} gap={"12px"} mt={{ lg: "24px", base: "44px" }}>
        <AspectRatio
          ratio={1}
          width={{ lg: "64px", base: "40px" }}
          height={{ lg: "64px", base: "40px" }}
          rounded="full"
          position="relative"
          flexShrink={0}
          flexGrow={0}
          overflow="hidden"
        >
          <Image
            src={data.picture.data?.attributes?.url ?? ""}
            alt={data.author}
            width={100}
            height={100}
            style={{
              objectPosition: "top",
            }}
          />
        </AspectRatio>

        <Flex direction="column" align={"start"} justify={"center"}>
          <Text
            fontSize={{ lg: "18px", base: "14px" }}
            lineHeight={{ lg: "18px", base: "14px" }}
            fontWeight="medium"
            data-testid={`testimonial.${data.id}.author`}
            color="#0D3050"
          >
            {data.author}
          </Text>
          <Text
            marginTop="4px"
            fontSize={{ lg: "14px", base: "12px" }}
            lineHeight={{ lg: "24px", base: "16px" }}
            letterSpacing="0.02em"
            data-testid={`testimonial.${data.id}.company`}
            color="rgba(13, 48, 80, 0.8)"
          >
            {data.jobTitle}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
}
